import { useGlobalPlayer } from '@tunasong/audio-ui'
import { graphHooks } from '@tunasong/graph-lib/react'
import { Close, Pause, Play } from '@tunasong/icons'
import { EmbedContent } from '@tunasong/plugin-embed/content'
import { parseLink } from '@tunasong/plugin-embed/parse-link'
import type { Audio, Persisted, PollOption } from '@tunasong/schemas'
import { isAudio } from '@tunasong/schemas'
import { Button, IconButton } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { createContext, useContext, useId, useState } from 'react'
import { serviceName } from './audio-candidates.js'
import { getOptionLabel } from './option-label.js'
import css from './option-audio.module.css'

interface PollAudio {
  /** The one option whose player window (an embed) is open, if any. */
  openId: string | null
  /** The hosted audio the app-wide player is playing right now, if any. */
  playingId: string | null
  /** Play hosted audio in the app-wide player, stopping whatever it was playing. Closes any open embed. */
  play(media: Persisted<Audio>[]): void
  pause(): void
  /** Opening an embed pauses the app-wide player, so the two never play over each other. */
  setOpenId(id: string | null): void
}

const PollAudioContext = createContext<PollAudio | null>(null)

/**
 * Per poll view: the one global-player hook and which option's embed window is open. One hook, not one per option
 * row: every `useGlobalPlayer` wires a controller to the shared <audio> element, so 175 rows would multiply
 * listeners.
 *
 * One source at a time. Hosted audio plays in the app-wide player, which has a single <audio> element, so a new
 * clip stops the last one wherever it was started. Embeds cannot play there, and cannot be stopped from outside
 * their iframe: opening one pauses the app-wide player, and playing hosted audio closes it.
 */
export const PollAudioProvider: FC<{ pollId: string; children: ReactNode }> = props => {
  const { pollId, children } = props
  const { playlist, isPlaying, controls } = useGlobalPlayer({ sourceEntityId: pollId })
  const [openId, setOpenId] = useState<string | null>(null)
  const value: PollAudio = {
    openId,
    playingId: isPlaying ? (playlist.active?.media.id ?? null) : null,
    play: media => {
      setOpenId(null)
      void playlist.playNow(media)
    },
    pause: () => controls.pause(),
    setOpenId: id => {
      if (id && isPlaying) {
        controls.pause()
      }
      setOpenId(id)
    },
  }
  return <PollAudioContext value={value}>{children}</PollAudioContext>
}

/** A link in the service's own embedded player; a link nothing can embed is offered as a link, inside the window. */
const ExternalPlayer: FC<{ service: string; url: string }> = props => {
  const { service, url } = props
  const embed = parseLink(url)
  if (!embed) {
    return (
      <a href={url} target="_blank" rel="noreferrer">
        Open on {serviceName(service)}
      </a>
    )
  }
  return <EmbedContent element={{ ...embed, size: 'small' }} />
}

/** Tooltip for a hosted clip's control: what pressing it does, or why it cannot be pressed. */
const hostedTitle = (hasLoaded: boolean, isPlayable: boolean, isPlaying: boolean) => {
  if (!hasLoaded) {
    return 'Loading audio…'
  }
  if (!isPlayable) {
    return 'This audio is not available'
  }
  return isPlaying ? 'Pause' : 'Play recording'
}

export interface OptionAudioProps {
  option: Pick<PollOption, 'name' | 'entityRef' | 'audio'>
}

/**
 * The option's play control. Hosted audio plays in the app-wide player (Play / Pause); a link opens a small window
 * under the option with the service's embed (Spotify, YouTube and the like), or the link itself when nothing can
 * embed it. Nothing leaves the app unasked.
 *
 * Never render this inside a <label> (see WithOptionAudio): a click anywhere in a label can activate its control,
 * so pressing play would cast or withdraw a vote. No wrapper element either: the toggle and the window are items
 * of the row they are rendered into, and only the window takes a line of its own.
 */
export const OptionAudio: FC<OptionAudioProps> = props => {
  const { option } = props
  const { audio } = option
  const poll = useContext(PollAudioContext)
  const id = useId()
  const { entity: linked } = graphHooks.useEntity(option.entityRef)
  const { entity: hosted } = graphHooks.useEntity(audio?.kind === 'entity' ? audio.entityId : undefined)
  /** What the option is called on screen, for the accessible names. */
  const label = getOptionLabel(option, linked?.name)

  if (!(audio && poll)) {
    return null
  }

  if (audio.kind === 'entity') {
    const clip = isAudio(hosted) && hosted.storageUrls?.url ? hosted : undefined
    const isPlaying = poll.playingId === audio.entityId
    const handlePlay = () => (isPlaying ? poll.pause() : clip && poll.play([clip]))
    return (
      <IconButton
        size="small"
        aria-label={`${isPlaying ? 'Pause' : 'Play'} ${label}`}
        title={hostedTitle(Boolean(hosted), Boolean(clip), isPlaying)}
        disabled={!clip}
        onClick={handlePlay}
      >
        {isPlaying ? <Pause /> : <Play />}
      </IconButton>
    )
  }

  const isOpen = poll.openId === id
  const handleToggle = () => poll.setOpenId(isOpen ? null : id)

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Play ${label}`}
        aria-expanded={isOpen}
        title={isOpen ? 'Close player' : `Play (${serviceName(audio.service)})`}
        onClick={handleToggle}
      >
        {isOpen ? <Close /> : <Play />}
      </IconButton>
      {isOpen ? (
        <div role="region" aria-label={`Player for ${label}`} className={css.player}>
          <ExternalPlayer service={audio.service} url={audio.url} />
        </div>
      ) : null}
    </>
  )
}

/**
 * A labelled control (vote button, radio, checkbox) with the option's audio beside it, outside the <label>.
 * Options without audio render the control alone, unchanged.
 */
export const WithOptionAudio: FC<{ option: PollOption; children: ReactNode }> = props => {
  const { option, children } = props
  if (!option.audio) {
    return children
  }
  return (
    <div className={css.row}>
      {children}
      <OptionAudio option={option} />
    </div>
  )
}

/** Queue every live option's hosted audio, in option order. External links cannot join the global player. */
export const PlayAllButton: FC<{ options: PollOption[] }> = props => {
  const { options } = props
  const player = useContext(PollAudioContext)
  const ids = options.flatMap(option =>
    !option.removed && option.audio?.kind === 'entity' ? [option.audio.entityId] : []
  )
  const { entities } = graphHooks.useEntitiesById<Audio>(ids)
  const byId = new Map(entities.filter(isAudio).map(entity => [entity.id, entity]))
  const queue = ids.flatMap(id => {
    const entity = byId.get(id)
    return entity ? [entity] : []
  })

  if (!player || queue.length === 0) {
    return null
  }
  return (
    <Button size="small" startIcon={<Play />} onClick={() => player.play(queue)}>
      Play all
    </Button>
  )
}
